import { Button } from '@design-system';
import { InviteClientCard, InviteLinkCard } from '@client/features/invite-client';
import { QuickPlanCard } from '@client/features/client-quick-plan';
import { StepCompletedOverlay, useInviteClientStep } from '@/features/advisor-onboarding';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { Mail, UserPlus, Link2, ClipboardList, ChevronRight, ArrowLeft } from 'lucide-react';

const INVITE_VIEW_CONTENT = {
  'invite-yourself': {
    title: 'Try it yourself',
    description: 'See exactly what your clients will experience',
  },
  'invite-client': {
    title: 'Invite a client',
    description: "They'll receive an email with a link to complete the intake form",
  },
} as const;

interface OptionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function Option({ icon, title, description, onClick }: OptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:border-primary hover:bg-primary/5 border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all"
    >
      <div className="group-hover:bg-primary text-foreground flex size-12 shrink-0 items-center justify-center rounded-full bg-gray-100 transition-all group-hover:text-white">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-foreground text-sm">{description}</p>
      </div>
      <ChevronRight className="group-hover:text-primary size-5 text-gray-400 transition-all" />
    </button>
  );
}

export function AdvisorOnboardingInviteClientStep() {
  const {
    advisorId,
    viewState,
    successType,
    successContent,
    isPendingDashboardNavigation,
    setViewState,
    handleBack,
    handleInviteSuccess,
    handleQuickPlanSuccess,
    handleGoToDashboard,
  } = useInviteClientStep();
  const { flags } = useFeatureFlags();

  const hideEmailAndFillIntakeForm = flags.hideInviteEmailAndFillIntakeForm ?? false;

  if (hideEmailAndFillIntakeForm) {
    return (
      <div className="relative flex flex-col items-center justify-center">
        <div className="w-full max-w-xl">
          <InviteLinkCard />
        </div>
      </div>
    );
  }

  if (
    !hideEmailAndFillIntakeForm &&
    (viewState === 'invite-yourself' || viewState === 'invite-client')
  ) {
    const { title, description } = INVITE_VIEW_CONTENT[viewState];

    return (
      <div className="relative flex flex-col items-center justify-center">
        <div className="w-full max-w-xl">
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
            >
              <ArrowLeft />
              Back
            </Button>
          </div>
          <InviteClientCard
            title={title}
            description={description}
            prefillWithAdvisor={viewState === 'invite-yourself'}
            onInviteSuccess={handleInviteSuccess}
          />
        </div>
        <StepCompletedOverlay
          isVisible={successType === 'invited'}
          title={successContent.title}
          description={successContent.description}
          buttonText="Go to dashboard"
          buttonLoading={isPendingDashboardNavigation}
          onContinue={handleGoToDashboard}
        />
      </div>
    );
  }

  if (viewState === 'quick-plan') {
    return (
      <div className="relative flex flex-col items-center justify-center">
        <div className="w-full max-w-xl">
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
            >
              <ArrowLeft />
              Back
            </Button>
          </div>
          <QuickPlanCard
            advisorId={advisorId}
            title="Quick Plan"
            description="Generate a plan instantly by entering a few details yourself."
            onSubmitSuccess={handleQuickPlanSuccess}
          />
        </div>
        <StepCompletedOverlay
          isVisible={successType === 'quick-plan'}
          title={successContent.title}
          description={successContent.description}
          buttonText="Go to dashboard"
          buttonLoading={isPendingDashboardNavigation}
          onContinue={handleGoToDashboard}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Create Your First Plan</h1>
          <p className="text-foreground mt-2">Pick how you'd like to get started</p>
        </div>

        <div className="space-y-3">
          <Option
            icon={<Mail className="size-5" />}
            title="Try it yourself first"
            description="Walk through the intake form as a client would"
            onClick={() => setViewState('invite-yourself')}
          />
          <Option
            icon={<UserPlus className="size-5" />}
            title="Invite a client"
            description="Email them an intake form to complete"
            onClick={() => setViewState('invite-client')}
          />
          <Option
            icon={<ClipboardList className="size-5" />}
            title="Quick plan"
            description="Enter basic info yourself to generate a plan instantly"
            onClick={() => setViewState('quick-plan')}
          />
        </div>
      </div>
    </div>
  );
}
